import { format } from 'date-fns'
import React from 'react'

import DoughnutChart from '@/compoments/charts/DoughnutChart'
import HorizontalBarChart from '@/compoments/charts/HorizontalBarChart'
import SelectBox from '@/compoments/common/SelectBox'

import {
    getStartAndEndOfMonth,
    getStartAndEndOfWeek,
    getStartAndEndOfYear,
} from '@/utils/helpers'
import { optionsCharts } from '@/utils/selectsOptionsConst'
import { DispatherDashboardQueryParams } from '@/types/dispatcher/query.params.types'
import { DispatcherDashboardData } from '@/types/dispatcher/dashboard.types'
import LineChart from '@/compoments/charts/LineChart'
import StackedBarChart from '@/compoments/charts/StackedBarChart'

type setParamsType = [
    'trendStartDate' | 'typeStartDate' | 'feedbackStartDate' | 'orderStartDate',
    'trendEndDate' | 'typeEndDate' | 'feedbackEndDate' | 'orderEndDate',
]

interface ChartsProps {
    data?: DispatcherDashboardData
    setQueryParams: React.Dispatch<
        React.SetStateAction<DispatherDashboardQueryParams>
    >
}
const Charts = ({ data, setQueryParams }: ChartsProps) => {
    const setParams = (
        date: Date,
        types: setParamsType,
        value: string = 'week',
    ) => {
        const { startDate: startDateWeek, endDate: endDateWeek } =
            getStartAndEndOfWeek(date)
        const { startDate: startDateMonth, endDate: endDateMonth } =
            getStartAndEndOfMonth(date)
        const { startDate: startDateYear, endDate: endDateYear } =
            getStartAndEndOfYear(date)
        setQueryParams((prev) => ({
            ...prev,
            [types[0]]:
                value === 'week'
                    ? startDateWeek
                    : value === 'month'
                      ? startDateMonth
                      : startDateYear,
            [types[1]]:
                value === 'week'
                    ? endDateWeek
                    : value === 'month'
                      ? endDateMonth
                      : endDateYear,
        }))
    }

    const handelSelectChange = (value: string, type: setParamsType) => {
        setParams(new Date(), type, value)
    }

    return (
        <>
            <div className="col-12">
                <div className="row gutters-18">
                    <div className="col-lg-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Job Trend
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            ['trendStartDate', 'trendEndDate'],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <div
                                    className="canvas-wrapper flex-auto"
                                    style={{ height: 215 }}
                                >
                                    <LineChart
                                        labels={
                                            data?.jobTrendStatistics?.data.map(
                                                (item) =>
                                                    format(
                                                        new Date(item.hour),
                                                        'dd MMM',
                                                    ),
                                            ) || []
                                        }
                                        values={
                                            data?.jobTrendStatistics?.data.map(
                                                (item) => item.count,
                                            ) || []
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Job by Job Type
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            ['typeStartDate', 'typeEndDate'],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <DoughnutChart
                                    labels={
                                        data?.jobTypeStatistics?.serviceStats?.map(
                                            (item) =>
                                                `${item.serviceName} ${item.percentage}`,
                                        ) || []
                                    }
                                    values={
                                        data?.jobTypeStatistics?.serviceStats?.map(
                                            (item) => item.count,
                                        ) || []
                                    }
                                    width={210}
                                    height={210}
                                    total={data?.jobTypeStatistics?.total}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="row gutters-18">
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Feedback
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            [
                                                'feedbackStartDate',
                                                'feedbackEndDate',
                                            ],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <DoughnutChart
                                    labels={Object.entries(
                                        data?.feedbackStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => key)}
                                    values={Object.entries(
                                        data?.feedbackStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => value)}
                                    units="percents"
                                    total={data?.feedbackStatistics?.total}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Jobs by Zone
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            ['orderStartDate', 'orderEndDate'],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <HorizontalBarChart
                                    labels={Object.keys(
                                        data?.zoneStatistics || {},
                                    )
                                        .filter((key) => key !== 'total')
                                        .map((key) => key)}
                                    values={Object.entries(
                                        data?.zoneStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Total Transactions
                                </div>
                                {/* <SelectBox
                                            wrapperClassName="select--transparent"
                                            options={options}
                                        /> */}
                            </div>
                            <div className="card-body">
                                <StackedBarChart
                                    labels={Object.keys(
                                        data?.transactions || {},
                                    ).map((key) => key)}
                                    values={[
                                        Object.values(
                                            data?.transactions || {},
                                        ).map((day) => day.thisWeek),
                                        Object.values(
                                            data?.transactions || {},
                                        ).map((day) => day.lastWeek),
                                        Object.values(
                                            data?.transactions || {},
                                        ).map((day) => day.cost),
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Charts
